import { inject, Injectable } from '@angular/core'
import { Observable } from 'rxjs'

import { switchMap, takeUntil } from 'rxjs/operators'

import { AuthService } from './auth.service'
import { LocalStorageShoppingCart } from './cart.service'
import { DestroyService } from './destroy.service'
import { shoppingCartApp, SHOPPING_CART_APP_TYPE } from './shopping-cart/shopping-cart.enum'
import { ShoppingCartService } from './shopping-cart/shopping-cart.service'
import { StorageService } from './storage.service'

@Injectable()
export class CartStorageService {
  private readonly authService = inject(AuthService)
  private readonly destroy$ = inject(DestroyService)
  private readonly shoppingCartService = inject(ShoppingCartService)
  private readonly storage = inject(StorageService)

  private readonly appType = inject(SHOPPING_CART_APP_TYPE)

  private storageKeyForShopingCart = 'shoping-cart'
  private storageKeyForGroupsAndPayments = 'shoping-cart-groups-and-payments'

  mergedBeats: LocalStorageShoppingCart[] = []

  getBeats() {
    return this.storage.load(this.storageKeyForShopingCart, [])
  }

  saveBeatsToLocalStorage(beats: LocalStorageShoppingCart[]): void {
    this.storage.save(this.storageKeyForShopingCart, beats)
  }

  synchronizeLocalAndServerShoppingCarts(
    getServerBeats: () => Observable<LocalStorageShoppingCart[]>,
    updateServerBeats: (beats: LocalStorageShoppingCart[]) => Observable<any>
  ) {
    getServerBeats()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(serverBeats => {
          const localBeats = this.getBeats()
          this.mergedBeats = this.mergeLocalAndServerBeats(localBeats, serverBeats)

          return updateServerBeats(this.mergedBeats)
        })
      )
      .subscribe({
        next: () => this.saveBeatsToLocalStorage(this.mergedBeats)
      })
  }

  mergeLocalAndServerBeats(localCartBeats: LocalStorageShoppingCart[], serverCartBeats: LocalStorageShoppingCart[]) {
    // Удаляем из serverCartBeats те элементы, которые уже есть в localCartBeats по beatId
    const filteredServerCartBeats = serverCartBeats.filter(
      serverBeat => !localCartBeats.some(localBeat => localBeat.beatId === serverBeat.beatId)
    )

    // Возвращаем объединённый массив: сначала server (уникальные), потом local (приоритет)
    return [...filteredServerCartBeats, ...localCartBeats]
  }

  addBeat(beatId: number, licenseId: string) {
    let localStorageBeats = this.getBeats()
    localStorageBeats = localStorageBeats.filter(b => b.beatId !== beatId)
    localStorageBeats.push({ beatId, licenseId })
    this.updateShoppingCart(localStorageBeats)
  }

  removeBeat(beatId: number) {
    const localStorageBeats = this.getBeats().filter(item => item.beatId !== beatId)
    this.updateShoppingCart(localStorageBeats)
  }

  updateBeat(beatId: number, licenseId: string) {
    let beats: LocalStorageShoppingCart[] = this.storage.load(this.storageKeyForShopingCart, [])

    beats = beats.map(item => {
      if (item.beatId === beatId) {
        item.licenseId = licenseId
      }
      return item
    })

    this.storage.save(this.storageKeyForShopingCart, beats)
    this.shoppingCartService.updateShoppingCart(beats)
  }

  updateShoppingCart(localStorageBeats: LocalStorageShoppingCart[]) {
    this.saveBeatsToLocalStorage(localStorageBeats)
    if (this.authService.isLoggedIn()) {
      if (this.appType === shoppingCartApp.Widget) {
        this.shoppingCartService.updateWidgetShoppingCart(localStorageBeats).subscribe()
      } else {
        this.shoppingCartService.updateShoppingCart(localStorageBeats).subscribe()
      }
    }
  }

  getPayments() {
    return this.storage.load(this.storageKeyForGroupsAndPayments, [])
  }

  savePayments(payments) {
    this.storage.save(this.storageKeyForGroupsAndPayments, payments)
  }
}
